import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../contexts/AuthContext';
import { Languages, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AuthPage = () => {
  const location = useLocation();
  const initialTab = location.pathname === '/register' ? 'register' : 'login';

  const [tab, setTab] = useState(initialTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const switchTab = (t) => {
    setTab(t);
    setLoading(false);
    navigate(t === 'login' ? '/login' : '/register', { replace: true });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password.length > 64) {
      toast.error('Password must be at most 64 characters');
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Registration failed';
      if (typeof detail === 'string') message = detail;
      else if (Array.isArray(detail) && detail.length > 0)
        message = detail[0].msg?.replace('Value error, ', '') || message;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 noise-overlay">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="auth-logo">
          <div className="w-12 h-12 rounded-md bg-primary/20 flex items-center justify-center">
            <Languages className="w-6 h-6 text-primary" />
          </div>
          <span className="font-heading text-2xl font-semibold text-foreground">
            <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
          </span>
        </Link>

        <Card className="bg-background-paper border-border" data-testid="auth-card">
          <CardContent className="pt-6 space-y-5">
            {/* Welcome text */}
            <div className="text-center space-y-1">
              <h2 className="font-heading text-2xl font-semibold text-foreground">
                Welcome to LanguageConstructor
              </h2>
              <p className="text-sm text-foreground-muted">
                {tab === 'login'
                  ? 'Sign in to access your conlangs'
                  : 'Create an account to start building languages'}
              </p>
            </div>

            {/* Tab selector */}
            <div className="grid grid-cols-2 rounded-lg bg-background p-1 border border-border" data-testid="auth-tab-selector">
              <button
                onClick={() => switchTab('login')}
                className={`rounded-md py-2 text-sm font-medium transition-colors ${
                  tab === 'login'
                    ? 'bg-background-paper text-foreground shadow-sm'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
                data-testid="auth-tab-login"
              >
                Login
              </button>
              <button
                onClick={() => switchTab('register')}
                className={`rounded-md py-2 text-sm font-medium transition-colors ${
                  tab === 'register'
                    ? 'bg-background-paper text-foreground shadow-sm'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
                data-testid="auth-tab-register"
              >
                Register
              </button>
            </div>

            {/* Forms */}
            {tab === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-background border-border focus:ring-primary"
                    required
                    autoComplete="email"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-foreground">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-background border-border focus:ring-primary"
                    required
                    autoComplete="current-password"
                    data-testid="login-password-input"
                  />
                </div>
                <div>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:text-primary/80"
                    data-testid="login-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading} data-testid="login-submit-btn">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</> : 'Sign In'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-foreground">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-background border-border focus:ring-primary"
                    required
                    autoComplete="name"
                    data-testid="register-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email" className="text-foreground">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-background border-border focus:ring-primary"
                    required
                    autoComplete="email"
                    data-testid="register-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password" className="text-foreground">Password</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-background border-border focus:ring-primary"
                    required
                    maxLength={64}
                    autoComplete="new-password"
                    data-testid="register-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-confirm" className="text-foreground">Confirm Password</Label>
                  <Input
                    id="reg-confirm"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-background border-border focus:ring-primary"
                    required
                    maxLength={64}
                    autoComplete="new-password"
                    data-testid="register-confirm-password-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading} data-testid="register-submit-btn">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating account...</> : 'Create Account'}
                </Button>
              </form>
            )}

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full bg-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background-paper px-2 text-foreground-subtle">Or continue with</span>
              </div>
            </div>

            {/* Google */}
            <Button
              variant="outline"
              className="w-full border-border hover:bg-accent"
              onClick={loginWithGoogle}
              data-testid="auth-google-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
